import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Clock } from "lucide-react";

interface HeatmapProps {
  accessTimestamps: string[];
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function Heatmap({ accessTimestamps }: HeatmapProps) {
  const heatmapData = useMemo(() => {
    // Initialize grid with zeros (7 days x 24 hours)
    const grid: number[][] = Array(7)
      .fill(0)
      .map(() => Array(24).fill(0));

    // Process each timestamp
    accessTimestamps.forEach((timestamp) => {
      const date = new Date(timestamp);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const hour = date.getHours();

      grid[dayOfWeek][hour] += 1;
    });

    return grid;
  }, [accessTimestamps]);

  // Find max value for color scaling
  const maxValue = useMemo(() => {
    let max = 0;
    heatmapData.forEach((row) => {
      row.forEach((value) => {
        if (value > max) max = value;
      });
    });
    return max || 1; // Avoid division by zero
  }, [heatmapData]);

  const getHeatmapColor = (value: number) => {
    if (value === 0) return "bg-muted";

    const intensity = value / maxValue;
    if (intensity > 0.7) return "bg-blue-600";
    if (intensity > 0.5) return "bg-blue-500";
    if (intensity > 0.3) return "bg-blue-400";
    if (intensity > 0.1) return "bg-blue-300";
    return "bg-blue-200";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-5" />
          Weekly Activity Heatmap
        </CardTitle>
        <CardDescription>
          Click activity by hour of the week (darker = more activity)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Hour labels */}
            <div className="flex">
              <div className="w-12" /> {/* Space for day labels */}
              {[0, 3, 6, 9, 12, 15, 18, 21].map((hour) => (
                <div
                  key={hour}
                  className="flex-1 text-center text-xs text-muted-foreground min-w-[32px]"
                >
                  {hour}h
                </div>
              ))}
            </div>

            {/* Heatmap grid */}
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="flex items-center gap-1 mb-1">
                <div className="w-12 text-xs text-muted-foreground text-right pr-2">
                  {day}
                </div>
                <div className="flex gap-1 flex-1">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const clicks = heatmapData[dayIndex][hour];
                    return (
                      <div
                        key={hour}
                        className={`w-full aspect-square rounded-sm ${getHeatmapColor(
                          clicks
                        )} transition-colors hover:ring-2 hover:ring-primary cursor-pointer min-w-[8px]`}
                        title={`${day} ${hour}:00 - ${clicks} clicks`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Legend */}
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
              <span>Less</span>
              <div className="flex gap-1">
                {["bg-muted", "bg-blue-200", "bg-blue-300", "bg-blue-400", "bg-blue-500", "bg-blue-600"].map(
                  (color, i) => (
                    <div key={i} className={`w-4 h-4 rounded-sm ${color}`} />
                  )
                )}
              </div>
              <span>More</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
